import { useCheckout } from '@stripe/react-stripe-js/checkout';
import { Environment } from '@util/Environment';
import { App } from 'hydra-data-types';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import { PurchaseSelector } from '../../store/purchase/PurchaseActions';
import { appOverride } from '../../util/AppUtils';
import { useIsPaymentRequiredForCheckout } from '../../util/Payments';
import * as images from '../assets/Images';
import { ContentLoading } from '../styles/ContentLoading';
import { Spacer } from '../styles/Dividers';
import { BaseErrorMessage } from '../styles/ErrorMessage';
import { NextButton } from '../styles/LayoutButtons';
import { TickList } from '../styles/List';
import { Heading4 } from '../styles/Typography';
import { CreditCardWhyNowButton, CreditCardWhyNowSection } from './CreditCardWhyNow';
import { Disclaimer } from './Disclaimer';
import { StripePaymentElement } from './StripePaymentElement';

export type CollectCardFormProps = {
  nextPage: (isNewUser?: boolean) => void;
  /**
   * Rendered inside a checkout card that supplies its own look (the `PaymentRedesign` experiment):
   * shorter heading and reassurance, no store reviews, and plain small print.
   */
  inCard?: boolean;
};

export const CollectCardForm: FunctionComponent<CollectCardFormProps> = (props) => {
  const formRef = useRef<null | HTMLFormElement>(null);
  const inCard = props.inCard === true;
  const checkout = useCheckout();
  const dispatch = useAppDispatch();

  const { purchase, hasTrial, hasFreeTrial } = useAppStore((state) => ({
    purchase: state.purchase,
    hasTrial: PurchaseSelector.hasTrial(state.purchase),
    hasFreeTrial: PurchaseSelector.hasFreeTrial(state.purchase),
  }));

  const [isWhyNowOpen, setIsWhyNowOpen] = useState(false);

  const loading = purchase.intent.loading || purchase.payment.loading;

  const isPaymentRequired = useIsPaymentRequiredForCheckout(checkout);

  const getButtonText = () => {
    if (hasTrial) return `Start ${hasFreeTrial ? 'free ' : ''}trial`;
    return 'Start now';
  };

  useEffect(
    function handlePaymentSuccess() {
      if (purchase.checkoutId !== undefined) {
        props.nextPage();
      }
    },
    [purchase.checkoutId, props.nextPage]
  );

  useEffect(
    function handlePaymentError() {
      if (purchase.payment.error) {
        formRef.current?.scrollIntoView();
      }
    },
    [purchase.payment.error]
  );

  function handleCardPurchase() {
    if (checkout.type !== 'success') {
      return;
    }

    dispatch({
      type: ActionType.CONFIRM_CHECKOUT,
      checkout: checkout.checkout,
    });
  }

  // User tried to submit form without clicking on button
  function preventDefaultFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
  }

  if (purchase.intent.loading || checkout.type === 'loading') {
    return <ContentLoading />;
  }

  return (
    <form data-test="collectCardForm" onSubmit={preventDefaultFormSubmit} ref={formRef}>
      {isPaymentRequired && (
        <>
          <BillingDetailsHeader>
            {inCard ? (
              <CardHeading>Payment details</CardHeading>
            ) : (
              <Heading4 style={{ textAlign: 'left', flex: 1 }}>Billing Details</Heading4>
            )}
            {hasFreeTrial && (
              <CreditCardWhyNowButton isOpen={isWhyNowOpen} setIsOpen={setIsWhyNowOpen} />
            )}
          </BillingDetailsHeader>

          <CreditCardWhyNowSection isOpen={isWhyNowOpen} />

          {/* The card says this under its button instead. */}
          {!inCard && (
            <PaymentSecurity>
              {hasTrial && (
                <>If you cancel any time before your trial ends you won’t be charged. </>
              )}
              All billing details are secure and encrypted.
            </PaymentSecurity>
          )}

          <StripePaymentElement />
        </>
      )}

      {purchase.payment.error && (
        <BaseErrorMessage data-test="cardError">{purchase.payment.error.message}</BaseErrorMessage>
      )}

      {/* The redesign shows its proof outside the checkout card. */}
      {Environment.APP === App.Nerva && !inCard && (
        <>
          <Spacer space={20} mobile={20} />
          <StoreReviewImageContainer>
            <img src={images.storeReviewRow} width="100%" alt="positive-store-reviews" />
          </StoreReviewImageContainer>
        </>
      )}

      <Spacer space={20} mobile={20} />
      {isPaymentRequired && <Disclaimer plain={inCard} />}
      {inCard && <Spacer space={16} mobile={16} />}
      <NextButton hideArrow onClick={handleCardPurchase} loading={loading} data-test="submitButton">
        {getButtonText()}
      </NextButton>
      {inCard ? (
        <CardReassurance>
          {hasTrial && <span>No commitment &mdash; cancel any time</span>}
          <span>Secure and encrypted</span>
        </CardReassurance>
      ) : (
        hasTrial && (
          <PaymentFeatures>
            <li>No commitment &mdash; cancel any time</li>
          </PaymentFeatures>
        )
      )}
    </form>
  );
};

const BillingDetailsHeader = styled.div`
  display: flex;
`;

const PaymentSecurity = styled.div`
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.xxs};
  margin-bottom: 1.5em;
  color: ${Color.content.default};
`;

const PaymentFeatures = styled(TickList)`
  margin: 1.75em 2em;
`;

// The card sets the colour, so these inherit it.
const CardHeading = styled(Heading4)`
  flex: 1;
  color: inherit;
  text-align: left;
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const CardReassurance = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ theme }) => `${theme.spacing._4px} ${theme.spacing._16px}`};
  margin-top: ${({ theme }) => theme.spacing._14px};
  font-size: 13px;
`;

const StoreReviewImageContainer = styled.div`
  background-color: ${appOverride({ nerva: Color.content.default, default: 'transparent' })};
  display: block;
  padding: ${appOverride({ nerva: '12px', default: '0' })};
  border-radius: ${appOverride({ nerva: '8px', default: '0' })};
`;
